import compact from 'lodash/compact'
import { GeckoEntry, GeckoKestrelModel } from 'types'

import type { AnnotationWithModel, RawRooftopAnnotation } from './types'

const CM2_TO_FT2_CONVERSION = 929.03

export const convertAreaCm2ToFt2 = (areaCm2: number): number =>
  areaCm2 / CM2_TO_FT2_CONVERSION

export const getAnnotationId = (annotation: RawRooftopAnnotation): string =>
  annotation.annotationId || annotation.id || ''

export const extractRooftopAnnotations = (
  kestrelModels: readonly GeckoEntry[]
): AnnotationWithModel[] => {
  return compact(
    kestrelModels.flatMap((model) => {
      // Model data annotations are dynamically typed, cast to expected rooftop annotation type
      const anns =
        (model.data?.annotations as RawRooftopAnnotation[] | undefined) ?? []
      return anns.map((a) => ({ a, modelId: model.id }))
    })
  )
}

export const filterPolygonAnnotations = (
  annotations: AnnotationWithModel[]
): AnnotationWithModel[] => {
  return annotations.filter(({ a }) => a.geometry?.kind === 'polygon')
}

export const calculateTotalDamagedArea = (
  polygonAnnotations: AnnotationWithModel[]
): number => {
  return polygonAnnotations.reduce((sum, { a }) => {
    const areaCm2 = a.area
    if (areaCm2) {
      return sum + convertAreaCm2ToFt2(areaCm2)
    }
    return sum
  }, 0)
}

export const getRoofAreaFromBoundingBox = (
  kestrelModel: GeckoKestrelModel | undefined
): number | null => {
  if (
    !kestrelModel?.data.boundingSegments ||
    !kestrelModel.data.measurementScale
  ) {
    return null
  }

  const segments = kestrelModel.data.boundingSegments
  const ueToMeters = kestrelModel.data.measurementScale / 100
  const meterToFeet = 3.28084

  let area = 0

  for (let i = 0; i < segments.length - 1; i++) {
    area +=
      0.5 *
      Math.abs(
        segments[i].x * segments[i + 1].y - segments[i + 1].x * segments[i].y
      )
  }

  const surfaceAreaMeters = area * ueToMeters * ueToMeters
  const surfaceAreaFeet = surfaceAreaMeters * meterToFeet * meterToFeet

  return surfaceAreaFeet
}
